import { FC, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import Middleware from '@/router/middleware/index.tsx';
import useBusinessOnboarding from '@/api/useBusinessOnboarding.ts';

const BusinessOnboarding: FC = () => {
  const { isLoading, session, currentUser, isLoadingUser } = useAuthContext();
  const location = useLocation();

  const email = session?.user?.email?.toLowerCase() ?? null;
  const isBusinessProvider = currentUser?.role === 'business_provider';
  const isBusinessOnboardingRoute = location.pathname === Routes.BUSINESS_ONBOARDING;

  // Only fetch onboarding data for business providers
  const { data, isLoading: isLoadingOnboarding, isError } = useBusinessOnboarding(email, isBusinessProvider);

  const shouldRedirectToOnboarding = useMemo(() => {
    // Wait for user data to load
    if (isLoadingUser) return false;
    // If not a business provider, don't force onboarding
    if (!isBusinessProvider) return false;
    // If already on onboarding page, don't redirect
    if (isBusinessOnboardingRoute) return false;
    // Wait for data to load
    if (isLoadingOnboarding || isError) return false;
    if (!email) return false;
    // Redirect to onboarding if no data exists
    return !data;
  }, [data, email, isError, isBusinessProvider, isLoadingOnboarding, isBusinessOnboardingRoute, isLoadingUser]);

  // Only show loading for business providers checking onboarding status
  const isMiddlewareLoading = isLoading || isLoadingUser || (isBusinessProvider && isLoadingOnboarding);

  return (
    <Middleware isLoading={isMiddlewareLoading}>
      {shouldRedirectToOnboarding ? <Navigate to={Routes.BUSINESS_ONBOARDING} replace /> : <Outlet />}
    </Middleware>
  );
};

export default BusinessOnboarding;
